import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Show, useUser } from "@clerk/react";
import { useQuery } from "@tanstack/react-query";
import { LazyVideo } from "@/components/ui/lazy-video";
import { VideoModal } from "@/components/ui/video-modal";
import { AuthModal } from "@/components/ui/auth-modal";
import { PupLogo } from "@/components/ui/pup-logo";
import { api, buildQueryString } from "@/lib/api";
import { registerGenerationId } from "@/hooks/use-generation";
import { usePiecanLink } from "@/hooks/use-piecan-link";
import { trackPiecanHandoffClick } from "@/services/analytics";
import { useUserStore } from "@/stores/user-store";
import { useFreeH3PromoStatus } from "@/pages/free-gen-page";
import type { Video } from "@/types";
import {
  Sparkles,
  ArrowRight,
  Play,
  BookOpen,
  DollarSign,
  Users,
  UserCircle,
  Gift,
  Loader2,
  ExternalLink,
} from "lucide-react";
import { ACADEMY_POSTS } from "@/pages/academy/academy-data";
import { cn } from "@/lib/utils";

const S3 = "https://animepup.s3.us-east-1.amazonaws.com/samples";

function PromptBar() {
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    navigate(
      `/animate-character${prompt.trim() ? `?prompt=${encodeURIComponent(prompt.trim())}` : ""}`,
    );
  };

  return (
    <form onSubmit={handleSubmit} className="relative w-full max-w-lg mx-auto">
      <div className="relative flex items-center rounded-2xl bg-white/6 border border-white/10 hover:border-white/15 focus-within:border-[#d4a574]/30 focus-within:shadow-[0_0_20px_rgba(212,165,116,0.08)] transition-all backdrop-blur-sm">
        <Play className="size-4 text-[#d4a574]/40 ml-4 shrink-0" />
        <input
          type="text"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder="Describe an anime scene or character to animate..."
          className="flex-1 bg-transparent px-3 py-3 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none"
        />
        <button
          type="submit"
          className="mr-1.5 px-4 py-1.5 rounded-xl btn-primary text-[12px] flex items-center gap-1.5"
        >
          <Sparkles className="size-3" />
          Create
        </button>
      </div>
    </form>
  );
}

const ACTIONS = [
  { href: "/animate-character", label: "Animate", icon: Play },
  { href: "/create-character", label: "Create", icon: UserCircle },
  { href: "/academy", label: "Academy", icon: BookOpen },
];

const SHOWCASE = [
  { video: `${S3}/Fights/1.mp4`, label: "Fight Scene" },
  { video: `${S3}/Scenery/2.mp4`, label: "Scenery" },
  { video: `${S3}/Surreal/1.mp4`, label: "Surreal" },
  { video: `${S3}/Fights/7.mp4`, label: "Action" },
  { video: `${S3}/Scenery/8.mp4`, label: "Landscape" },
  { video: `${S3}/Surreal/4.mp4`, label: "Creative" },
  { video: `${S3}/Fights/3.mp4`, label: "Battle" },
  { video: `${S3}/Scenery/1.mp4`, label: "Atmosphere" },
];

function VideoShowcase() {
  const [activeVideo, setActiveVideo] = useState<string | null>(null);

  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
        {SHOWCASE.map((item, i) => (
          <button
            key={i}
            onClick={() => setActiveVideo(item.video)}
            className="group rounded-2xl overflow-hidden relative aspect-[4/3] animate-fade-in-up"
            style={{ animationDelay: `${i * 60}ms` }}
          >
            <LazyVideo
              src={item.video}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent group-hover:from-black/30 transition-colors" />
            <div className="absolute bottom-0 left-0 right-0 p-3 flex items-end justify-between">
              <span className="text-[10px] font-semibold text-white/70 group-hover:text-white transition-colors">
                {item.label}
              </span>
              <Play className="size-4 text-white/40 group-hover:text-white/80 transition-colors" />
            </div>
          </button>
        ))}
      </div>
      {activeVideo && (
        <VideoModal src={activeVideo} onClose={() => setActiveVideo(null)} />
      )}
    </div>
  );
}

const FEATURED_POSTS = ACADEMY_POSTS.slice(0, 4);

function FeaturedAcademy() {
  return (
    <section className="px-5 md:px-8 pb-12 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-lg md:text-xl font-bold text-foreground tracking-tight">
            Learn & Create
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Prompting guides and workflows from AnimePup Academy
          </p>
        </div>
        <Link
          to="/academy"
          className="hidden sm:flex items-center gap-1 text-[11px] font-semibold text-[#d4a574] hover:text-[#e0b584] transition-colors"
        >
          View all <ArrowRight className="size-3" />
        </Link>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {FEATURED_POSTS.map((post, i) => (
          <Link
            key={post.slug}
            to={`/academy/${post.slug}`}
            className={cn(
              "group block rounded-xl card-glass border border-white/6 overflow-hidden hover:border-white/12 transition-all",
              "animate-fade-in-up",
            )}
            style={{ animationDelay: `${i * 75}ms` }}
          >
            <div className="aspect-[16/10] overflow-hidden">
              <img
                src={post.coverImage}
                alt={post.title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                loading="lazy"
              />
            </div>
            <div className="p-3.5">
              <span className="text-[9px] font-bold text-[#d4a574]/60 uppercase tracking-wider">
                {post.categoryLabel}
              </span>
              <h3 className="text-[12px] font-bold text-foreground leading-snug mt-1 line-clamp-2 group-hover:text-[#d4a574] transition-colors">
                {post.title}
              </h3>
              <p className="text-[10px] text-muted-foreground mt-1">
                {post.readTime}
              </p>
            </div>
          </Link>
        ))}
      </div>

      <div className="sm:hidden mt-4 text-center">
        <Link
          to="/academy"
          className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#d4a574]"
        >
          View all articles <ArrowRight className="size-3" />
        </Link>
      </div>
    </section>
  );
}

const AFFILIATE_FORM_URL =
  "https://docs.google.com/forms/d/e/1FAIpQLSe0bVD4XMmA-kpOVJHs27VeiBexBIcekzmPkp63s1tJyK_g7A/viewform?usp=publish-editor";

function AffiliateSection() {
  return (
    <section className="px-5 md:px-8 pb-12 max-w-4xl mx-auto">
      <div className="rounded-2xl border border-white/6 card-glass overflow-hidden">
        <div className="p-6 md:p-8 md:flex items-center gap-8">
          <div className="flex-1 mb-6 md:mb-0">
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-[10px] font-bold uppercase tracking-wider mb-3">
              <Users className="size-3" /> Affiliate Program
            </div>
            <h2 className="text-lg md:text-xl font-bold text-foreground tracking-tight mb-2">
              Earn <span className="text-emerald-400">30%</span> lifetime
              revenue
            </h2>
            <p className="text-xs text-muted-foreground leading-relaxed max-w-md">
              Share AnimePup with your audience and earn 30% of every dollar
              they spend — for the entire lifetime of their account. No cap on
              earnings, no expiry.
            </p>
          </div>
          <div className="flex flex-col gap-2.5 shrink-0">
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              <DollarSign className="size-3.5 text-emerald-400 shrink-0" />
              <span>30% of all revenue, forever</span>
            </div>
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              <DollarSign className="size-3.5 text-emerald-400 shrink-0" />
              <span>Payouts via PayPal or crypto</span>
            </div>
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              <DollarSign className="size-3.5 text-emerald-400 shrink-0" />
              <span>No minimum payout threshold</span>
            </div>
            <div className="flex gap-2 mt-2">
              <a
                href={AFFILIATE_FORM_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary text-[12px] py-2 px-5 inline-flex items-center gap-1.5 rounded-xl"
              >
                Apply Now <ArrowRight className="size-3" />
              </a>
              <Link
                to="/affiliates"
                className="btn-glass text-[12px] py-2 px-5 inline-flex items-center rounded-xl"
              >
                Learn more
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function MyRecentCreations() {
  const { user, isSignedIn } = useUser();
  const { hasPurchased } = useUserStore();
  const { data } = useQuery({
    queryKey: ["home-recent-videos", user?.id],
    queryFn: async () => {
      if (!user?.id) throw new Error("No user");
      return api.get<{ success: boolean; videos: Video[] }>(
        `/videos${buildQueryString({ user_id: user.id, limit: "6" })}`,
      );
    },
    enabled: !!user?.id && isSignedIn,
    refetchInterval: (query) => {
      const vids = query.state.data?.videos ?? [];
      const busy = vids.some((v) => v.status === "pending" || v.status === "processing");
      return busy ? 3000 : false;
    },
    refetchIntervalInBackground: true,
  });

  const videos = data?.videos ?? [];
  const inFlight = videos.filter((v) => v.status === "pending" || v.status === "processing");
  const completed = videos.filter((v) => v.status === "completed" && (v.url || v.watermarked_url));
  const recent = [...inFlight, ...completed].slice(0, 3);

  const inFlightIds = inFlight.map((v) => v.id).join(",");
  useEffect(() => {
    if (!inFlightIds) return;
    for (const id of inFlightIds.split(",")) registerGenerationId(id);
  }, [inFlightIds]);

  if (!isSignedIn || recent.length === 0) return null;

  return (
    <section className="px-5 md:px-8 pb-14 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-lg md:text-xl font-bold text-foreground tracking-tight">
            Your Recent Creations
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            {inFlight.length > 0 ? "Generating — updates automatically" : "Pick up where you left off"}
          </p>
        </div>
        <Link
          to="/assets"
          className="flex items-center gap-1 text-[11px] font-semibold text-[#d4a574] hover:text-[#e0b584] transition-colors"
        >
          My Creations <ArrowRight className="size-3" />
        </Link>
      </div>
      <div className="grid grid-cols-3 gap-2.5">
        {recent.map((v) => {
          const isBusy = v.status === "pending" || v.status === "processing";
          if (isBusy) {
            return (
              <Link
                key={v.id}
                to={`/generation/${v.id}`}
                className="group rounded-2xl overflow-hidden relative aspect-square bg-white/5 border border-white/8 flex flex-col items-center justify-center gap-2"
              >
                <Loader2 className="size-6 text-[#d4a574] animate-spin" />
                <span className="text-[11px] font-semibold text-muted-foreground">Generating…</span>
              </Link>
            );
          }
          const displayUrl = hasPurchased
            ? (v.url || v.watermarked_url)
            : (v.watermarked_url || v.url);
          return (
            <Link
              key={v.id}
              to="/assets"
              className="group rounded-2xl overflow-hidden relative aspect-square bg-white/5 border border-white/8"
            >
              {v.media_type === "IMAGE" ? (
                <img src={displayUrl ?? undefined} alt="" className="w-full h-full object-cover" loading="lazy" />
              ) : (
                <LazyVideo src={displayUrl ?? ""} className="w-full h-full object-cover" />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent group-hover:from-black/30 transition-colors" />
            </Link>
          );
        })}
      </div>
      {!hasPurchased && (
        <Link
          to="/billing"
          className="mt-4 btn-primary w-full py-3 rounded-xl flex items-center justify-center gap-2 text-sm"
        >
          <Sparkles className="size-4" />
          Buy a pack to generate!
          <ArrowRight className="size-4" />
        </Link>
      )}
    </section>
  );
}

function PiecanFallbackNotice({ userId }: { userId: string }) {
  const { data, isLoading } = usePiecanLink(userId);

  if (isLoading || !data?.eligible || !data.url) return null;

  return (
    <section className="relative px-5 md:px-8 pt-6 max-w-2xl mx-auto animate-fade-in-up">
      <a
        href={data.url}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => trackPiecanHandoffClick(userId, "home_notice")}
        className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 rounded-2xl border border-white/15 bg-white/5 px-5 py-4 hover:border-white/25 transition-colors"
      >
        <div className="size-11 rounded-xl bg-white/10 text-foreground flex items-center justify-center shrink-0">
          <ExternalLink className="size-5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-0.5">
            New AI models
          </p>
          <p className="text-[17px] font-extrabold text-foreground tracking-tight">
            Use our latest AI models on PieCan
          </p>
          <p className="text-[12px] text-muted-foreground mt-0.5">
            Keep creating on PieCan with your premium access included.
          </p>
        </div>
        <span className="inline-flex items-center gap-1.5 text-[13px] font-bold text-foreground bg-white/10 px-3.5 py-2 rounded-xl shrink-0 self-start sm:self-center">
          Open PieCan
          <ArrowRight className="size-3.5" />
        </span>
      </a>
    </section>
  );
}

export function HomePage() {
  const { user } = useUser();
  const { hasPurchased } = useUserStore();
  const { data: promo } = useFreeH3PromoStatus(user?.id);
  const showFreeGenNotice = !!promo?.eligible && hasPurchased;
  // Eligibility (incl. the support allowlist) is decided by /partner/piecan-link;
  // PiecanFallbackNotice renders nothing unless that endpoint says the user is
  // eligible, so only users who will get acc_tier 1 on PieCan ever see PieCan.
  const showPiecanNotice = !!user?.id;

  return (
    <div className="overflow-hidden">
      {showPiecanNotice && <PiecanFallbackNotice userId={user.id} />}
      {showFreeGenNotice && (
        <section className="relative px-5 md:px-8 pt-6 max-w-2xl mx-auto animate-fade-in-up">
          <Link
            to="/free-gen"
            className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 rounded-2xl border border-[#d4a574]/35 bg-gradient-to-r from-[#d4a574]/15 via-[#d4a574]/10 to-transparent px-5 py-4 hover:border-[#d4a574]/55 transition-colors"
          >
            <div className="size-11 rounded-xl bg-[#d4a574] text-[#1a1520] flex items-center justify-center shrink-0">
              <Gift className="size-5" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[11px] font-bold uppercase tracking-wider text-[#d4a574] mb-0.5">
                Limited time · Purchasers only
              </p>
              <p className="text-[17px] font-extrabold text-foreground tracking-tight">
                Free Generation — 15 Second
              </p>
              <p className="text-[12px] text-muted-foreground mt-0.5">
                One free Power video with sound. Claim it before it&apos;s gone.
              </p>
            </div>
            <span className="inline-flex items-center gap-1.5 text-[13px] font-bold text-[#1a1520] bg-[#d4a574] px-3.5 py-2 rounded-xl shrink-0 self-start sm:self-center">
              Claim free gen
              <ArrowRight className="size-3.5" />
            </span>
          </Link>
        </section>
      )}

      {/* Hero */}
      <section className="relative px-5 md:px-8 pt-12 md:pt-20 pb-8 max-w-2xl mx-auto text-center">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[350px] h-[200px] bg-[#d4a574]/5 rounded-full blur-[80px] pointer-events-none" />

        <div className="relative">
          <div className="flex justify-center mb-5 animate-fade-in-up">
            <div className="relative">
              <div className="absolute inset-0 rounded-full bg-[#d4a574] blur-[50px] scale-[4] hidden md:block" />
              <PupLogo className="size-14 relative" />
            </div>
          </div>

          <h1 className="text-3xl md:text-5xl font-extrabold text-foreground tracking-tight leading-snug mb-3 animate-fade-in-up delay-100">
            Make your own <span className="text-gradient">Anime</span>
          </h1>

          <p className="text-muted-foreground text-[13px] md:text-sm max-w-sm mx-auto mb-8 animate-fade-in-up delay-200">
            Create and animate your own anime characters, all with AI.
          </p>

          <div className="animate-fade-in-up delay-300 mb-6">
            <PromptBar />
          </div>

          <div className="flex items-center justify-center gap-2 flex-wrap animate-fade-in delay-400">
            {ACTIONS.map((a) => {
              const Icon = a.icon;
              return (
                <Link
                  key={a.href}
                  to={a.href}
                  className="px-4 py-1.5 rounded-full border border-white/8 bg-white/4 text-[12px] font-semibold text-muted-foreground hover:text-foreground hover:border-white/15 hover:bg-white/8 transition-all flex items-center gap-1.5"
                >
                  <Icon className="size-3" />
                  {a.label}
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Your recent creations (signed-in, has generations) */}
      <MyRecentCreations />

      {/* Video Showcase */}
      <section className="px-5 md:px-8 pb-14 max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="text-lg md:text-xl font-bold text-foreground tracking-tight">
              Made with AnimePup
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              AI-generated anime scenes and characters
            </p>
          </div>
          <Link
            to="/explore"
            className="hidden sm:flex items-center gap-1 text-[11px] font-semibold text-[#d4a574] hover:text-[#e0b584] transition-colors"
          >
            Explore more <ArrowRight className="size-3" />
          </Link>
        </div>
        <VideoShowcase />
        <div className="sm:hidden mt-4 text-center">
          <Link
            to="/explore"
            className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#d4a574]"
          >
            Explore more <ArrowRight className="size-3" />
          </Link>
        </div>
      </section>

      {/* Featured Academy Posts */}
      <FeaturedAcademy />

      {/* Affiliate Program */}
      <AffiliateSection />

      {/* Bottom CTA */}
      <section className="px-5 md:px-8 pb-16 max-w-md mx-auto">
        <div className="rounded-2xl p-6 md:p-8 text-center card-glass border border-white/6">
          <p className="text-base font-bold text-foreground mb-1">
            Start creating
          </p>
          <p className="text-[12px] text-muted-foreground mb-5">
            84 free gizmos on signup
          </p>
          <Show when="signed-out">
            <CtaAuthButton />
          </Show>
          <Show when="signed-in">
            <Link
              to="/animate-character"
              className="btn-primary text-sm py-2.5 px-6 inline-flex items-center gap-2 rounded-xl"
            >
              Animate Character <ArrowRight className="size-3.5" />
            </Link>
          </Show>
        </div>
      </section>
    </div>
  );
}

function CtaAuthButton() {
  const [showAuth, setShowAuth] = useState(false);
  return (
    <>
      <button
        onClick={() => setShowAuth(true)}
        className="btn-primary text-sm py-2.5 px-6 flex items-center gap-2 mx-auto rounded-xl"
      >
        <Sparkles className="size-3.5" />
        Get Started Free
      </button>
      <AuthModal isOpen={showAuth} onClose={() => setShowAuth(false)} />
    </>
  );
}
